import React, { useState, useEffect } from "react";
import TrainingAPI from "../../services/TrainingAPI";
import MemberViewAPI from "../../services/MemberViewAPI";
import { useUser } from "../../context/UserContext";
import RefreshButton from "../../common/RefreshButton";
import LoadingSpinner from "../../common/LoadingSpinner";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "../css/SafetyScoreCard.css";

/**
 * Formats a category string to display-ready format.
 * Converts hyphenated names like "social-engineering" to "Social Engineering".
 */
const formatCategoryForDisplay = (category) => {
  if (!category) return "General";
  if (category.includes(" ")) return category;
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * TrainingDialGauge — SVG semi-circle gauge with one notch per lesson.
 * Completed notches are highlighted; the current (next) lesson is accented.
 */
function TrainingDialGauge({ total, completed, hasRequiredTrainings, t }) {
  const cx = 160;
  const cy = 160;
  const r = 144;
  // The arc sweeps from 180° (left) to 0° (right) — top half of a circle
  const startAngleDeg = 180;

  const toRad = (deg) => (deg * Math.PI) / 180;

  // Convert polar angle to SVG x,y (angles in degrees, 0=right, CCW)
  const polarToXY = (angleDeg, radius) => ({
    x: cx + radius * Math.cos(toRad(angleDeg)),
    y: cy - radius * Math.sin(toRad(angleDeg)),
  });

  if (total === 0) {
    return (
      <div className="training-dial-wrap">
        <span className="shield-score-value">
          {t("dashboard.training.getStarted")}
        </span>
        <span className="shield-score-period">
          {t("dashboard.training.noLessons")}
        </span>
      </div>
    );
  }

  if (completed >= total) {
    // Full arc in gold — all done
    const notches = Array.from({ length: total });
    return (
      <div className="training-dial-wrap">
        <svg
          viewBox="0 0 320 180"
          className="training-dial-svg"
          aria-hidden="true"
        >
          {/* Background track arc */}
          <path
            d={`M ${polarToXY(180, r).x} ${polarToXY(180, r).y} A ${r} ${r} 0 0 1 ${polarToXY(0, r).x} ${polarToXY(0, r).y}`}
            fill="none"
            stroke="var(--color-section-border)"
            strokeWidth="10"
            strokeLinecap="round"
          />
          {/* Filled arc — all complete */}
          <path
            d={`M ${polarToXY(180, r).x} ${polarToXY(180, r).y} A ${r} ${r} 0 0 1 ${polarToXY(0, r).x} ${polarToXY(0, r).y}`}
            fill="none"
            stroke="var(--color-shield-strong-bg, #22c55e)"
            strokeWidth="10"
            strokeLinecap="round"
          />
          {/* Notches */}
          {notches.map((_, i) => {
            const angle = startAngleDeg - (i / Math.max(total - 1, 1)) * 180;
            const inner = polarToXY(angle, r - 7);
            const outer = polarToXY(angle, r + 7);
            return (
              <line
                key={i}
                x1={inner.x}
                y1={inner.y}
                x2={outer.x}
                y2={outer.y}
                stroke="var(--color-card-bg, white)"
                strokeWidth="8"
                strokeLinecap="round"
              />
            );
          })}
          {/* Centre label */}
          <text
            x={cx}
            y={cy - 6}
            textAnchor="middle"
            className="dial-center-main"
          >
            ✓
          </text>
          <text
            x={cx}
            y={cy + 14}
            textAnchor="middle"
            className="dial-center-sub"
          >
            {t("dashboard.training.allDone")}
          </text>
        </svg>
        <span className="shield-score-period">
          {t("dashboard.training.completedAll").replace("{total}", total)}
        </span>
      </div>
    );
  }

  // Normal in-progress state
  const currentLesson = completed + 1; // 1-based lesson number being worked on
  // Fraction of arc filled = completed lessons
  const filledFraction = completed / total;
  // End angle of the filled portion (sweeping left→right, i.e. 180→0)
  const filledEndDeg = startAngleDeg - filledFraction * 180;
  const filledEnd = polarToXY(filledEndDeg, r);
  const trackStart = polarToXY(180, r);
  const trackEnd = polarToXY(0, r);
  const largeArcFilled = filledFraction > 0.5 ? 1 : 0;

  return (
    <div className="training-dial-wrap">
      <svg
        viewBox="0 0 320 180"
        className="training-dial-svg"
        aria-hidden="true"
      >
        {/* Background track */}
        <path
          d={`M ${trackStart.x} ${trackStart.y} A ${r} ${r} 0 0 1 ${trackEnd.x} ${trackEnd.y}`}
          fill="none"
          stroke="var(--color-section-border)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        {/* Filled portion */}
        {completed > 0 && (
          <path
            d={`M ${trackStart.x} ${trackStart.y} A ${r} ${r} 0 ${largeArcFilled} 1 ${filledEnd.x} ${filledEnd.y}`}
            fill="none"
            stroke="var(--color-btn-primary-bg, #3b82f6)"
            strokeWidth="10"
            strokeLinecap="round"
          />
        )}
        {/* Notch marks — one per lesson */}
        {Array.from({ length: total }).map((_, i) => {
          const angle = startAngleDeg - (i / Math.max(total - 1, 1)) * 180;
          const inner = polarToXY(angle, r - 7);
          const outer = polarToXY(angle, r + 7);
          const isDone = i < completed;
          const isCurrent = i === completed;
          return (
            <line
              key={i}
              x1={inner.x}
              y1={inner.y}
              x2={outer.x}
              y2={outer.y}
              stroke={
                isCurrent
                  ? "var(--color-btn-primary-bg, #3b82f6)"
                  : isDone
                    ? "var(--color-card-bg, white)"
                    : "var(--color-text-tertiary, #94a3b8)"
              }
              strokeWidth={isCurrent ? 10 : 8}
              strokeLinecap="round"
              opacity={isDone ? 0.7 : 1}
            />
          );
        })}
        {/* Current lesson dot on arc */}
        <circle
          cx={filledEnd.x}
          cy={filledEnd.y}
          r={6}
          fill="var(--color-btn-primary-bg, #3b82f6)"
          stroke="var(--color-card-bg, white)"
          strokeWidth="2"
        />
        {/* Centre text */}
        <text
          x={cx}
          y={cy + 8}
          textAnchor="middle"
          className="dial-center-main"
        >
          {completed + "/" + total + " Lessons"}
        </text>
      </svg>
    </div>
  );
}

function TrainingProgress({ setHeroContent }) {
  const { t } = useTranslation();
  const { isReadOnlyView, selectedUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Training progress state from API
  const [progressData, setProgressData] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [categoryStats, setCategoryStats] = useState({});

  useEffect(() => {
    fetchTrainingProgress();
  }, [isReadOnlyView, selectedUser?.uuid]);

  const fetchTrainingProgress = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (isReadOnlyView && selectedUser?.uuid) {
        // Owner viewing a consented member — use owner-access proxy endpoint
        const response = await MemberViewAPI.getMemberTraining(
          selectedUser.uuid,
        );
        if (response && response.success) {
          const memberTrainings = response.trainings || [];
          setTrainings(memberTrainings);

          // Derive progress stats from the training list
          const completedCount = memberTrainings.filter(
            (t) => t.userStatus === "COMPLETED",
          ).length;
          setProgressData({
            totalTrainings: memberTrainings.length,
            completedTrainings: completedCount,
            requiredTrainingsTotal: 0,
            requiredTrainingsCompleted: 0,
            recommendedTrainingsTotal: 0,
            recommendedTrainingsCompleted: 0,
            electiveTrainingsCompleted: 0,
            averageQuizScore: null,
          });

          // Build category stats from the list
          const stats = {};
          memberTrainings.forEach((training) => {
            const categoryList = training.category || "General";
            const rawCategory = categoryList.split(",")[0].trim() || "General";
            const category = formatCategoryForDisplay(rawCategory);
            if (!stats[category]) {
              stats[category] = { total: 0, completed: 0 };
            }
            stats[category].total++;
            if (training.userStatus === "COMPLETED") {
              stats[category].completed++;
            }
          });
          setCategoryStats(stats);
        } else {
          throw new Error("Unable to retrieve member training data");
        }
        return;
      }

      // Standard path: fetch progress + full trainings list in parallel
      const [progressRes, trainingsRes] = await Promise.all([
        TrainingAPI.getUserProgress(),
        TrainingAPI.getAllTrainings(),
      ]);

      if (progressRes.success) {
        setProgressData(progressRes.progress);
      }

      if (trainingsRes.success) {
        setTrainings(trainingsRes.trainings || []);

        // Calculate category statistics from trainings
        // Use only the first category from comma-separated list as the main category
        const stats = {};
        (trainingsRes.trainings || []).forEach((training) => {
          const categoryList = training.category || "General";
          // Get only the first category (main category) and format for display
          const rawCategory = categoryList.split(",")[0].trim() || "General";
          const category = formatCategoryForDisplay(rawCategory);
          if (!stats[category]) {
            stats[category] = { total: 0, completed: 0 };
          }
          stats[category].total++;
          if (training.userStatus === "COMPLETED") {
            stats[category].completed++;
          }
        });
        setCategoryStats(stats);
      }
    } catch (err) {
      console.error("[TrainingProgress] Error fetching data:", err);
      setError(err.message || "Failed to fetch training progress");
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const [showAreas, setShowAreas] = useState(false);

  // Calculate progress from real data - prioritize required trainings
  const requiredTotal = progressData?.requiredTrainingsTotal || 0;
  const requiredCompleted = progressData?.requiredTrainingsCompleted || 0;
  const hasRequiredTrainings = requiredTotal > 0;

  // Derive completed count directly from the trainings list for accuracy
  const actualCompleted = trainings.filter(
    (t) => t.userStatus === "COMPLETED",
  ).length;
  const actualTotal = trainings.length || progressData?.totalTrainings || 0;

  // Fall back to overall if no required trainings configured
  const total = hasRequiredTrainings ? requiredTotal : actualTotal;
  const completed = hasRequiredTrainings ? requiredCompleted : actualCompleted;
  const progressPercentage = total > 0 ? (completed / total) * 100 : 0;

  // Additional metrics for display
  const recommendedTotal = progressData?.recommendedTrainingsTotal || 0;
  const recommendedCompleted = progressData?.recommendedTrainingsCompleted || 0;
  const electiveCompleted = progressData?.electiveTrainingsCompleted || 0;
  const totalCompleted = progressData?.completedTrainings || 0;
  const totalTrainings = progressData?.totalTrainings || 0;

  // Use average quiz score if available, otherwise use completion percentage
  const displayScore = progressData?.averageQuizScore
    ? Math.round(progressData.averageQuizScore)
    : Math.round(progressPercentage);

  const getProgressLevel = () => {
    if (progressPercentage >= 80) return "good";
    if (progressPercentage >= 50) return "warning";
    return "danger";
  };

  const categoryIcons = {
    "Email Safety": "📧",
    "Social Engineering": "🎭",
    "Password Security": "🔐",
    General: "📚",
    Privacy: "🔒",
    "Mobile Security": "📱",
    "Phone Call Safety": "📞",
    "Social Media Safety": "👥",
  };

  const getCategoryIcon = (category) => {
    return categoryIcons[category] || "📖";
  };

  const getScoreExplanation = (score) => {
    if (total === 0) return t("dashboard.training.explain0");
    if (score >= 90) return t("dashboard.training.explain90");
    if (score >= 80) return t("dashboard.training.explain80");
    if (score >= 70) return t("dashboard.training.explain70");
    if (score >= 60) return t("dashboard.training.explain60");
    if (score >= 50) return t("dashboard.training.explain50");
    return t("dashboard.training.explainDefault");
  };

  const handleRefresh = () => {
    fetchTrainingProgress(true);
  };

  if (loading && !progressData) {
    return (
      <div className="simple-card safety-score-card">
        <h2 className="simple-card-title">{t("dashboard.training.title")}</h2>
        <LoadingSpinner
          message={t("common.loading")}
          size="md"
          className="compact"
        />
      </div>
    );
  }

  if (error && !progressData) {
    return (
      <div className="simple-card safety-score-card">
        <h2 className="simple-card-title">{t("dashboard.training.title")}</h2>
        <div className="safety-error-message">
          <p className="safety-error-text">{t("common.unableToLoad")}</p>
          <button
            onClick={() => fetchTrainingProgress()}
            className="safety-retry-btn"
          >
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="simple-card safety-score-card">
      <div className="safety-card-header">
        <h2 className="simple-card-title">{t("dashboard.training.title")}</h2>
        <RefreshButton
          onClick={handleRefresh}
          loading={isRefreshing}
          size="sm"
        />
      </div>

      {/* Main Focus — Semi-circle dial showing lesson progress */}
      <div className="shield-score-hero">
        <TrainingDialGauge
          total={total}
          completed={completed}
          hasRequiredTrainings={hasRequiredTrainings}
          t={t}
        />
      </div>

      {/* Score Explanation */}
      <p className="shield-explanation">{getScoreExplanation(displayScore)}</p>

      {/* Continue Training CTA — front and center (#3) */}
      {completed < total && total > 0 && (
        <div className="shield-cta-container">
          <button
            className="shield-cta-button"
            onClick={() => setHeroContent && setHeroContent("Training Center")}
            disabled={isReadOnlyView}
            title={
              isReadOnlyView ? "Not available in read-only view" : undefined
            }
          >
            {t("dashboard.training.continueBtn")}
          </button>
        </div>
      )}

      {/* Training Breakdown - Required vs Recommended */}
      {(hasRequiredTrainings || recommendedTotal > 0) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            marginTop: "1rem",
            padding: "0.75rem",
            backgroundColor: "var(--progress-bg)",
            borderRadius: "8px",
          }}
        >
          {hasRequiredTrainings && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  color:
                    requiredCompleted === requiredTotal ? "#4caf50" : "#173997",
                }}
              >
                {requiredCompleted}/{requiredTotal}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#666" }}>
                {t("dashboard.training.required")}
              </div>
            </div>
          )}
          {recommendedTotal > 0 && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "600",
                  color: "#ff9800",
                }}
              >
                {recommendedCompleted}/{recommendedTotal}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#666" }}>
                {t("dashboard.training.recommended")}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quiz Stats - show if user has taken quizzes */}
      {progressData?.totalQuizzesTaken > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            marginTop: "1rem",
            padding: "0.75rem",
            backgroundColor: "var(--progress-bg)",
            borderRadius: "8px",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "#3e8fdf",
              }}
            >
              {progressData.totalQuizzesTaken}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary, #666)",
              }}
            >
              {t("dashboard.training.quizzesTaken")}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "#4caf50",
              }}
            >
              {Math.round(progressData.averageQuizScore || 0)}%
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary, #666)",
              }}
            >
              {t("dashboard.training.avgScore")}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "#ff9800",
              }}
            >
              {progressData.totalBadgesEarned || 0}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary, #666)",
              }}
            >
              {t("dashboard.training.badges")}
            </div>
          </div>
        </div>
      )}

      {/* Training Areas - collapsible to show progress in each category */}
      {Object.keys(categoryStats).length > 0 && (
        <div className="shield-areas-section">
          <button
            className="shield-areas-toggle"
            onClick={() => setShowAreas(!showAreas)}
          >
            {showAreas ? "▼" : "▶"}{" "}
            {t("dashboard.training.areasToggle").replace(
              "{count}",
              Object.keys(categoryStats).length,
            )}
          </button>
          {showAreas && (
            <ul className="simple-activity-list">
              {Object.entries(categoryStats).map(([category, stats]) => {
                const isCompleted =
                  stats.completed === stats.total && stats.total > 0;
                return (
                  <li key={category} className="simple-activity-item">
                    <span className="simple-activity-icon">
                      {isCompleted ? "✅" : getCategoryIcon(category)}
                    </span>
                    <span className="simple-activity-text">{category}</span>
                    <span
                      className={`simple-activity-date ${
                        isCompleted ? "simple-status-good" : ""
                      }`}
                    >
                      {stats.completed}/{stats.total}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default TrainingProgress;
